import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { SiteHeader } from "@/components/SiteHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

export default function CustomerRegister() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user && user.role === "customer") return <Navigate to="/account" replace />;
  if (user && user.role === "admin") return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim() || !email.trim() || !password) return;
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    setSubmitting(true);
    const res = await register({
      email: email.trim().toLowerCase(),
      password,
      name: name.trim(),
      phone: phone.trim(),
    });
    setSubmitting(false);
    if (!res.ok) return toast.error(res.error);
    toast.success(`Welcome, ${res.user?.name?.split(" ")[0] || ""}!`);
    navigate("/account");
  };

  return (
    <div data-testid="customer-register-page" className="min-h-screen grain-overlay">
      <SiteHeader />
      <main className="max-w-md mx-auto px-6 py-16">
        <p className="label-eyebrow">Join The Cozy Bean</p>
        <h1 className="mt-3 font-serif-display text-4xl sm:text-5xl tracking-tighter">
          Create your account.
        </h1>
        <p className="mt-3 text-sm text-[#4A4341]">
          Save your favorite drinks, reorder in one tap, and follow your latest order live.
        </p>

        <form onSubmit={submit} className="mt-8 space-y-5">
          <div>
            <Label htmlFor="reg-name">Name</Label>
            <Input
              id="reg-name"
              data-testid="register-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
              className="mt-1 bg-white border-black/10 focus-visible:ring-1 focus-visible:ring-[#C05A46]"
            />
          </div>
          <div>
            <Label htmlFor="reg-phone">Phone</Label>
            <Input
              id="reg-phone"
              data-testid="register-phone-input"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="717.555.5555"
              autoComplete="tel"
              className="mt-1 bg-white border-black/10 focus-visible:ring-1 focus-visible:ring-[#C05A46] font-mono-jet"
            />
          </div>
          <div>
            <Label htmlFor="reg-email">Email</Label>
            <Input
              id="reg-email"
              type="email"
              data-testid="register-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              className="mt-1 bg-white border-black/10 focus-visible:ring-1 focus-visible:ring-[#C05A46]"
            />
          </div>
          <div>
            <Label htmlFor="reg-password">Password</Label>
            <Input
              id="reg-password"
              type="password"
              data-testid="register-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              className="mt-1 bg-white border-black/10 focus-visible:ring-1 focus-visible:ring-[#C05A46]"
            />
            <p className="mt-1 text-[11px] text-[#7B7573]">At least 8 characters.</p>
          </div>
          <Button
            type="submit"
            disabled={submitting}
            data-testid="register-submit-btn"
            className="btn-brand w-full rounded-full py-6 text-sm font-semibold inline-flex items-center justify-center gap-2"
          >
            {submitting ? "Creating account…" : "Create account"}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </form>

        <div className="mt-8 flex items-center justify-between text-sm">
          <Link to="/signin" data-testid="register-to-signin-link" className="text-[#C05A46] hover:underline">
            I already have an account
          </Link>
          <Link to="/order" data-testid="register-guest-link" className="text-[#7B7573] hover:text-[#1A1514] transition-colors">
            Continue as guest →
          </Link>
        </div>
      </main>
    </div>
  );
}
